import { useEffect, useState } from "react";
import { api, fmtDate, formatApiError, API_URL } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { SearchCombobox } from "@/components/SearchCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, FileDown, FlaskConical, Mail } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { customer_id: "", product_description: "", size: "", material: "Silicone",
  quotation_no: "", pro_forma_no: "", po_no: "", invoice_no: "", parameters: [], features: [], note: "" };

export default function TestCertificates() {
  const [certs, setCerts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [defaults, setDefaults] = useState({ parameters: [], features: [], note: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/test-certificates").then((r) => setCerts(r.data));
  const pager = usePager(certs);
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/sales-orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/test-certificates/defaults").then((r) => setDefaults(r.data));
  }, []);

  useEffect(() => {
    const invId = new URLSearchParams(window.location.search).get("invoice");
    if (invId && defaults.parameters.length && !open) {
      api.get(`/test-certificates/prefill/${invId}`).then((r) => {
        setEditing(null);
        setForm({ ...emptyForm, ...r.data, features: [...defaults.features], note: defaults.note });
        setOpen(true);
        window.history.replaceState({}, "", "/test-certificates");
      }).catch((e) => toast.error(formatApiError(e)));
    }
  }, [defaults]);

  const openNew = () => {
    setEditing(null);
    setOrderId("");
    setForm({ ...emptyForm, features: [...defaults.features], note: defaults.note });
    setOpen(true);
  };
  const fillFromOrder = async (soId) => {
    setOrderId(soId);
    try {
      const r = await api.get(`/test-certificates/prefill-order/${soId}`);
      setForm((f) => ({ ...f, ...r.data }));
      toast.success("Details auto-filled from order");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (c) => {
    setEditing(c);
    setOrderId("");
    setForm({ customer_id: c.customer_id, product_description: c.product_description || "",
      size: c.size || "", material: c.material || "Silicone",
      quotation_no: c.quotation_no || "", pro_forma_no: c.pro_forma_no || "",
      po_no: c.po_no || "", invoice_no: c.invoice_no || "",
      parameters: (c.parameters || []).map((p, i) =>
        defaults.parameters.some((m) => m.name === p.name) ? p : { ...p, _key: `k${i}` }),
      features: c.features || [], note: c.note || "" });
    setOpen(true);
  };

  const isSelected = (name) => form.parameters.some((p) => p.name === name);
  const toggleParam = (m) => {
    setForm((f) => isSelected(m.name)
      ? { ...f, parameters: f.parameters.filter((p) => p.name !== m.name) }
      : { ...f, parameters: [...f.parameters, { ...m }] });
  };
  const setParam = (name, key, val) => {
    setForm((f) => ({ ...f, parameters: f.parameters.map((p) => p.name === name ? { ...p, [key]: val } : p) }));
  };
  const addParam = () => setForm((f) => ({ ...f, parameters: [...f.parameters, { name: "", unit: "", specified: "", observed: "", _key: `k${Date.now()}` }] }));
  const setCustomParam = (key, patch) => setForm((f) => ({ ...f, parameters: f.parameters.map((p) => p._key === key ? { ...p, ...patch } : p) }));
  const removeCustomParam = (key) => setForm((f) => ({ ...f, parameters: f.parameters.filter((p) => p._key !== key) }));

  const save = async () => {
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    if (!form.product_description.trim()) { toast.error("Enter product description"); return; }
    const params = form.parameters.filter((p) => p.name.trim()).map(({ _key, ...p }) => p);
    if (!params.length) { toast.error("Select at least one test parameter"); return; }
    const body = { ...form, parameters: params, features: form.features.filter((x) => x.trim()) };
    try {
      if (editing) {
        await api.put(`/test-certificates/${editing.id}`, body);
        toast.success("Certificate updated");
      } else {
        const r = await api.post("/test-certificates", body);
        toast.success(`Test Certificate ${r.data.number} created`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const emailCert = async (c) => {
    if (!window.confirm(`Email certificate ${c.number} (PDF attached) to the customer?`)) return;
    const tid = toast.loading("Sending certificate…");
    try {
      const r = await api.post(`/test-certificates/${c.id}/email`);
      toast.success(`Certificate emailed to ${r.data.to}`, { id: tid });
    } catch (e) { toast.error(formatApiError(e), { id: tid }); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete certificate ${c.number}?`)) return;
    try { await api.delete(`/test-certificates/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="test-certificates-page">
      <PageHeader title="Test Certificates" subtitle="Material test certificates (MTC) on your letterhead"
        right={<Button onClick={openNew} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="tc-new-btn"><Plus size={15} className="mr-1" /> New Certificate</Button>} />

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm" data-testid="tc-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Certificate</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">Product</th>
              <th className="text-left px-4 py-2.5">Invoice</th>
              <th className="text-right px-4 py-2.5">Parameters</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors duration-200" data-testid={`tc-row-${c.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs font-semibold">{c.number}</td>
                <td className="px-4 py-2.5 text-xs">{fmtDate(c.date)}</td>
                <td className="px-4 py-2.5">{c.customer_snapshot?.company_name}</td>
                <td className="px-4 py-2.5 text-xs">{c.product_description}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{c.invoice_no || "—"}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{(c.parameters || []).length}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => window.open(`${API_URL}/test-certificates/${c.id}/pdf`, "_blank")} title="PDF"
                            className="p-1.5 text-slate-500 hover:text-[#3B82F6] transition-colors duration-200" data-testid={`tc-pdf-${c.id}`}><FileDown size={13} /></button>
                    <button onClick={() => emailCert(c)} title="Email certificate PDF to customer"
                            className="p-1.5 text-indigo-600 hover:text-indigo-800 transition-colors duration-200" data-testid={`tc-email-${c.id}`}><Mail size={13} /></button>
                    <button onClick={() => edit(c)} title="Edit"
                            className="p-1.5 text-slate-500 hover:text-[#3B82F6] transition-colors duration-200" data-testid={`tc-edit-${c.id}`}><Pencil size={13} /></button>
                    <button onClick={() => del(c)} title="Delete"
                            className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors duration-200" data-testid={`tc-del-${c.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {certs.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-sm text-slate-500">No test certificates yet — click "New Certificate".</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <FlaskConical size={18} className="text-[#3B82F6]" />
              {editing ? `Edit Certificate ${editing.number}` : "New Test Certificate"}
              <span className="ml-2 text-[11px] font-normal px-2 py-0.5 rounded-full bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20" data-testid="tc-param-count">
                {form.parameters.length} parameter{form.parameters.length === 1 ? "" : "s"} selected
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 pb-16">
            {/* LEFT: certificate details */}
            <div className="lg:col-span-2 space-y-5">
              <div className="border border-slate-200 rounded-lg p-4 space-y-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">1 · Customer & Product</div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Order <span className="normal-case tracking-normal font-normal text-slate-400">(auto-fills everything)</span></Label>
                  <SearchCombobox testId="tc-order" placeholder="Select order to auto-fill" searchPlaceholder="Search orders…" emptyText="No sales orders yet"
                    label={(() => { const o = orders.find((x) => x.id === orderId); return o ? `${o.number} — ${o.customer_snapshot?.company_name || ""}` : ""; })()}
                    options={orders.map((o) => ({ id: o.id, label: `${o.number} — ${o.customer_snapshot?.company_name || ""}`, sub: o.po_number || "" }))}
                    onSelect={fillFromOrder} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Customer *</Label>
                  <SearchCombobox testId="tc-customer" placeholder="Select customer" searchPlaceholder="Search customers…" emptyText="No customers yet"
                    label={customers.find((c) => c.id === form.customer_id)?.company_name}
                    options={customers.map((c) => ({ id: c.id, label: c.company_name, sub: c.city || "" }))}
                    onSelect={(v) => setForm({ ...form, customer_id: v })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Product Description *</Label>
                  <Input value={form.product_description} onChange={(e) => setForm({ ...form, product_description: e.target.value })} placeholder="e.g. Silicone Sponge Insulation Sleeve" className="mt-1.5" data-testid="tc-product" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Size</Label>
                    <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="ID 35 x OD 55 mm" className="mt-1.5" data-testid="tc-size" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Material</Label>
                    <Input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className="mt-1.5" data-testid="tc-material" />
                  </div>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">2 · References <span className="normal-case tracking-normal font-normal text-slate-400">(optional)</span></div>
                <div className="grid grid-cols-2 gap-3">
                  {[["quotation_no", "Quotation No"], ["pro_forma_no", "Pro Forma No"], ["po_no", "Purchase Order"], ["invoice_no", "Invoice No"]].map(([k, lbl]) => (
                    <div key={k}>
                      <Label className="text-xs uppercase tracking-widest text-slate-500">{lbl}</Label>
                      <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 h-9" data-testid={`tc-${k}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-4 space-y-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">4 · Features & Note</div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Features <span className="normal-case tracking-normal text-slate-400">(one per line)</span></Label>
                  <Textarea value={form.features.join("\n")} onChange={(e) => setForm({ ...form, features: e.target.value.split("\n") })} rows={6} className="mt-1.5 text-sm" data-testid="tc-features" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Note</Label>
                  <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={4} className="mt-1.5 text-sm" data-testid="tc-note" />
                </div>
              </div>
            </div>

            {/* RIGHT: parameters table */}
            <div className="lg:col-span-3">
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">3 · Test Parameters *</div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={addParam}
                            className="text-[11px] px-2 py-1 rounded border border-[#3B82F6]/30 text-[#3B82F6] font-semibold hover:bg-white transition-colors duration-200" data-testid="tc-add-param">+ Add Parameter</button>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, parameters: [...defaults.parameters.map((m) => f.parameters.find((p) => p.name === m.name) || { ...m }), ...f.parameters.filter((p) => p._key)] }))}
                            className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-white transition-colors duration-200" data-testid="tc-select-all">Select all</button>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, parameters: [] }))}
                            className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-white transition-colors duration-200" data-testid="tc-clear-all">Clear</button>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-white text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-100">
                    <tr>
                      <th className="w-10 px-3 py-2"></th>
                      <th className="text-left px-2 py-2">Parameter</th>
                      <th className="text-left px-2 py-2 w-[26%]">Specified Value</th>
                      <th className="text-left px-2 py-2 w-[30%]">Observed Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defaults.parameters.map((m) => {
                      const sel = form.parameters.find((p) => p.name === m.name);
                      const tid = m.name.replace(/[^a-zA-Z0-9]+/g, "-");
                      return (
                        <tr key={m.name} className={`border-b border-slate-50 transition-colors duration-200 ${sel ? "bg-[#3B82F6]/[0.03]" : "hover:bg-slate-50/60"}`}>
                          <td className="px-3 py-2 text-center align-middle">
                            <Checkbox checked={!!sel} onCheckedChange={() => toggleParam(m)} data-testid={`tc-param-${tid}`} />
                          </td>
                          <td className="px-2 py-2 align-middle cursor-pointer" onClick={() => toggleParam(m)}>
                            <span className={`font-medium ${sel ? "text-[#3B82F6]" : "text-slate-700"}`}>{m.name}</span>
                            {m.unit && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500">{m.unit}</span>}
                          </td>
                          <td className="px-2 py-1.5 align-middle">
                            {sel ? (
                              <Input value={sel.specified} onChange={(e) => setParam(m.name, "specified", e.target.value)} placeholder="—" className="h-8 text-sm bg-white" data-testid={`tc-spec-${tid}`} />
                            ) : <span className="text-slate-300 text-xs pl-1">—</span>}
                          </td>
                          <td className="px-2 py-1.5 align-middle">
                            {sel ? (
                              <div className="relative">
                                <Input value={sel.observed} onChange={(e) => setParam(m.name, "observed", e.target.value)} placeholder={m.unit ? `value in ${m.unit}` : "value"} className={`h-8 text-sm bg-white ${m.unit ? "pr-16" : ""}`} data-testid={`tc-obs-${tid}`} />
                                {m.unit && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[#3B82F6] pointer-events-none">{m.unit}</span>}
                              </div>
                            ) : <span className="text-slate-300 text-xs pl-1">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {form.parameters.filter((p) => p._key !== undefined).map((p) => (
                      <tr key={p._key} className="border-b border-slate-50 bg-[#3B82F6]/[0.03]" data-testid={`tc-custom-${p._key}`}>
                        <td className="px-3 py-2 text-center align-middle">
                          <button type="button" onClick={() => removeCustomParam(p._key)} className="p-1 text-slate-400 hover:text-rose-600 transition-colors duration-200" title="Remove parameter" data-testid={`tc-custom-del-${p._key}`}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                        <td className="px-2 py-1.5 align-middle">
                          <div className="flex gap-1.5">
                            <Input value={p.name} onChange={(e) => setCustomParam(p._key, { name: e.target.value })} placeholder="Parameter name" className="h-8 text-sm bg-white flex-1" data-testid={`tc-custom-name-${p._key}`} />
                            <Input value={p.unit} onChange={(e) => setCustomParam(p._key, { unit: e.target.value })} placeholder="Unit" className="h-8 text-sm bg-white w-20" data-testid={`tc-custom-unit-${p._key}`} />
                          </div>
                        </td>
                        <td className="px-2 py-1.5 align-middle">
                          <Input value={p.specified} onChange={(e) => setCustomParam(p._key, { specified: e.target.value })} placeholder="—" className="h-8 text-sm bg-white" data-testid={`tc-custom-spec-${p._key}`} />
                        </td>
                        <td className="px-2 py-1.5 align-middle">
                          <Input value={p.observed} onChange={(e) => setCustomParam(p._key, { observed: e.target.value })} placeholder={p.unit ? `value in ${p.unit}` : "value"} className="h-8 text-sm bg-white" data-testid={`tc-custom-obs-${p._key}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white/95 backdrop-blur border-t border-slate-200 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="tc-save">{editing ? "Save Changes" : "Create Certificate"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
